import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Copy, Search, ShieldCheck, Power, PowerOff, KeyRound, Check, X, Mail, Link2 } from "lucide-react";
import { InviteDialog, InvitationsCard, ResetLinkDialog } from "@/components/AdminInvitations";

const ROLES = [
  { value: "gestionnaire_ge", label: "Gestionnaire GE", help: "Suivi opérationnel, validations, planning" },
  { value: "finance",         label: "Finance",         help: "Facturation, encaissements, exports comptables" },
  { value: "auditeur",        label: "Auditeur",        help: "Lecture seule : journal, exports, non-opérationnel" },
  { value: "medical",         label: "Médical",         help: "Suivi médical / visites, scope restreint" },
  { value: "salarie",         label: "Salarié",         help: "Mis à disposition — à rattacher à un dossier salarié ensuite" },
  { value: "employeur",       label: "Employeur",       help: "Entreprise utilisatrice — préférez /register" },
  { value: "admin",           label: "Administrateur",  help: "Accès complet — à réserver aux super-administrateurs" },
];

const TERRITOIRES = ["La Réunion", "Mayotte", "Martinique", "Guadeloupe", "Guyane", "Saint-Martin", "Saint-Barthélemy", "Nouvelle-Calédonie", "Polynésie française", "Métropole"];

function genTempPassword() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let s = "";
  const buf = new Uint32Array(14);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  for (let i = 0; i < buf.length; i++) s += alphabet[buf[i] % alphabet.length];
  return s + "!";
}

const EMPTY = { email: "", name: "", role: "gestionnaire_ge", territoire: "", entreprise_id: "", password: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("tous");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleMotif, setToggleMotif] = useState("");
  const [resetApprove, setResetApprove] = useState(null);
  const [resetTempPwd, setResetTempPwd] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [invKey, setInvKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e, r] = await Promise.all([
        api.get("/auth/users/all"),
        api.get("/entreprises").catch(() => ({ data: [] })),
        api.get("/auth/password-resets", { params: { statut: "en_attente" } }).catch(() => ({ data: [] })),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setEntreprises(Array.isArray(e.data) ? e.data : []);
      setResets(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error("Impossible de charger la liste des comptes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== "tous" && u.role !== roleFilter) return false;
      if (statusFilter === "actifs" && u.is_active === false) return false;
      if (statusFilter === "desactives" && u.is_active !== false) return false;
      if (!needle) return true;
      return (u.email || "").toLowerCase().includes(needle) || (u.name || "").toLowerCase().includes(needle);
    });
  }, [users, q, roleFilter, statusFilter]);

  const toggleActive = async () => {
    if (!toggleTarget) return;
    if (toggleTarget.is_active !== false && !toggleMotif.trim()) {
      toast.error("Motif de désactivation obligatoire");
      return;
    }
    try {
      await api.patch(`/auth/users/${toggleTarget.id}/active`, {
        is_active: toggleTarget.is_active === false,
        motif: toggleMotif.trim() || undefined,
      });
      toast.success(toggleTarget.is_active === false ? "Compte réactivé" : "Compte désactivé — accès coupé, traçabilité conservée");
      setToggleTarget(null);
      setToggleMotif("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opération impossible");
    }
  };

  const approveReset = async () => {
    if (!resetApprove || !resetTempPwd) return;
    try {
      const r = await api.post(`/auth/password-resets/${resetApprove.id}/approve`, { new_password: resetTempPwd });
      setResetApprove(null);
      setResetTempPwd("");
      setLastCreated({ email: r.data.user_email, role: "reset", password: r.data.temp_password });
      toast.success("Reset approuvé — mot de passe temporaire à transmettre");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approbation impossible");
    }
  };

  const rejectReset = async (reset) => {
    const motif = window.prompt("Motif de rejet (obligatoire) :");
    if (!motif) return;
    try {
      await api.post(`/auth/password-resets/${reset.id}/reject`, { new_password: "x".repeat(10), motif });
      toast.success("Demande rejetée");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rejet impossible");
    }
  };

  const submit = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("Email, nom et mot de passe temporaire sont requis");
      return;
    }
    if (form.role === "salarie" && !form.entreprise_id) {
      // salarié : entreprise de rattachement très recommandée (sinon pending_attachment)
      const ok = window.confirm("Aucune entreprise de rattachement choisie pour ce salarié. Il sera créé en attente de rattachement. Continuer ?");
      if (!ok) return;
    }
    setCreating(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        password: form.password,
        ...(form.territoire ? { territoire: form.territoire } : {}),
        ...(form.entreprise_id ? { entreprise_id: form.entreprise_id } : {}),
      };
      const r = await api.post("/auth/users-create", payload);
      setLastCreated({ ...r.data, password: form.password });
      toast.success("Compte créé — mot de passe temporaire à transmettre au destinataire");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Création impossible";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt).then(() => toast.success("Copié")); };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Comptes utilisateurs</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Création des comptes privilégiés (gestionnaire, finance, auditeur, médical, administrateur) et des comptes salariés rattachés. L'inscription publique <code className="px-1 py-0.5 rounded bg-slate-100 text-xs">/register</code> est réservée aux entreprises utilisatrices.
          </p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" data-testid="admin-users-invite-btn" onClick={() => setInviteOpen(true)}>
          <Mail className="h-4 w-4 mr-2" /> Inviter par e-mail
        </Button>
        <Button data-testid="admin-users-add-btn" onClick={() => { setForm({ ...EMPTY, password: genTempPassword() }); setOpen(true); }} className="bg-[#1B2A4A] text-white hover:bg-[#243458]">
          <UserPlus className="h-4 w-4 mr-2" /> Nouveau compte
        </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Comptes existants ({filtered.length})</CardTitle>
          <div className="flex flex-col md:flex-row gap-3 mt-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input data-testid="admin-users-search" placeholder="Rechercher par email ou nom…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-users-status-filter" className="md:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Actifs + désactivés</SelectItem>
                <SelectItem value="actifs">Actifs seulement</SelectItem>
                <SelectItem value="desactives">Désactivés seulement</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="admin-users-role-filter" className="md:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les rôles</SelectItem>
                {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-sm text-slate-500">Chargement…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b">
                  <tr><th className="text-left py-2">Email</th><th className="text-left py-2">Nom</th><th className="text-left py-2">Rôle</th><th className="text-left py-2">Territoire</th><th className="text-left py-2">Statut</th><th className="text-right py-2">Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} data-testid={`admin-users-row-${u.id}`} className={`border-b last:border-b-0 hover:bg-slate-50 ${u.is_active === false ? "opacity-60" : ""}`}>
                      <td className="py-2 font-mono text-xs">{u.email}</td>
                      <td className="py-2">{u.name || "—"}</td>
                      <td className="py-2"><Badge variant="outline">{ROLES.find(r => r.value === u.role)?.label || u.role}</Badge></td>
                      <td className="py-2 text-slate-600">{u.territoire || "—"}</td>
                      <td className="py-2">
                        {u.is_active === false
                          ? <div className="space-y-0.5"><Badge variant="destructive">Désactivé</Badge>{u.deactivation_reason && <div className="text-xs text-slate-500">{u.deactivation_reason}</div>}</div>
                          : u.invitation_en_attente ? <Badge className="bg-amber-100 text-amber-800" data-testid={`admin-users-invite-pending-${u.id}`}>Invitation en attente</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-800">Actif</Badge>}
                      </td>
                      <td className="py-2 text-right space-x-2 whitespace-nowrap">
                        {u.is_active !== false && <Button size="sm" variant="outline" data-testid={`admin-users-reset-link-${u.id}`} onClick={() => setLinkTarget(u)}>
                          <Link2 className="h-3 w-3 mr-1" />Mot de passe</Button>}
                        <Button size="sm" variant="outline"
                                data-testid={`admin-users-toggle-${u.id}`}
                                onClick={() => { setToggleTarget(u); setToggleMotif(""); }}>
                          {u.is_active === false
                            ? <><Power className="h-3 w-3 mr-1" />Réactiver</>
                            : <><PowerOff className="h-3 w-3 mr-1" />Désactiver</>}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-sm text-slate-500">Aucun compte ne correspond aux filtres.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <InvitationsCard refreshKey={invKey} />
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} roles={ROLES} territoires={TERRITOIRES} entreprises={entreprises}
        onDone={() => { setInvKey((k) => k + 1); load(); }} />
      <ResetLinkDialog target={linkTarget} onClose={() => setLinkTarget(null)} onDone={() => setInvKey((k) => k + 1)} />

      <Card data-testid="admin-password-resets-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> Demandes de réinitialisation de mot de passe ({resets.length} en attente)</CardTitle>
        </CardHeader>
        <CardContent>
          {resets.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune demande en attente. Les utilisateurs peuvent soumettre une demande via <code className="px-1 py-0.5 rounded bg-slate-100 text-xs">POST /api/auth/password-reset/request</code>.</p>
          ) : (
            <div className="divide-y">
              {resets.map(r => (
                <div key={r.id} data-testid={`reset-row-${r.id}`} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs">{r.email}</div>
                    <div className="text-xs text-slate-500">Demandé le {new Date(r.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid={`reset-approve-${r.id}`}
                            onClick={() => { setResetApprove(r); setResetTempPwd(genTempPassword()); }}
                            className="bg-emerald-600 text-white hover:bg-emerald-700">
                      <Check className="h-3 w-3 mr-1" />Approuver
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`reset-reject-${r.id}`} onClick={() => rejectReset(r)}>
                      <X className="h-3 w-3 mr-1" />Rejeter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="admin-users-dialog">
          <DialogHeader><DialogTitle>Créer un compte utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Rappel : le compte reçoit <strong>obligatoirement</strong> le mot de passe temporaire par un canal sécurisé. Il devra le changer à la première connexion. L'inscription publique <code>/register</code> est bloquée pour ces rôles.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Rôle</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-users-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}><div className="flex flex-col"><span>{r.label}</span><span className="text-xs text-slate-500">{r.help}</span></div></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Email professionnel</Label><Input data-testid="admin-users-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="prenom.nom@oscopge.fr" /></div>
              <div className="space-y-2"><Label>Nom affiché</Label><Input data-testid="admin-users-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Prénom NOM" /></div>
              <div className="space-y-2">
                <Label>Territoire (optionnel)</Label>
                <Select value={form.territoire || "none"} onValueChange={v => setForm({ ...form, territoire: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="admin-users-territoire"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{TERRITOIRES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === "salarie" && (
                <div className="space-y-2">
                  <Label>Entreprise de rattachement</Label>
                  <Select value={form.entreprise_id || "none"} onValueChange={v => setForm({ ...form, entreprise_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="admin-users-entreprise"><SelectValue placeholder="Choisir une entreprise…" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— (compte en attente de rattachement)</SelectItem>{entreprises.map(e => <SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2 md:col-span-2">
                <Label>Mot de passe temporaire</Label>
                <div className="flex gap-2">
                  <Input data-testid="admin-users-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="font-mono" />
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: genTempPassword() })} data-testid="admin-users-gen-pwd">Générer</Button>
                  <Button type="button" variant="outline" onClick={() => copy(form.password)} data-testid="admin-users-copy-pwd"><Copy className="h-4 w-4" /></Button>
                </div>
                <p className="text-xs text-slate-500">À transmettre par un canal sécurisé (pas par email public). Le destinataire devra le changer à la première connexion.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="admin-users-submit" onClick={submit} disabled={creating} className="bg-[#1B2A4A] text-white">{creating ? "Création…" : "Créer le compte"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toggleTarget} onOpenChange={v => { if (!v) { setToggleTarget(null); setToggleMotif(""); } }}>
        <DialogContent data-testid="admin-users-toggle-dialog">
          <DialogHeader><DialogTitle>{toggleTarget?.is_active === false ? "Réactiver le compte" : "Désactiver le compte"}</DialogTitle></DialogHeader>
          {toggleTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 border p-3">
                <div><span className="text-slate-500">Email :</span> <code className="ml-2 font-mono">{toggleTarget.email}</code></div>
                <div><span className="text-slate-500">Rôle :</span> <Badge className="ml-2">{ROLES.find(r => r.value === toggleTarget.role)?.label || toggleTarget.role}</Badge></div>
              </div>
              {toggleTarget.is_active !== false ? (
                <>
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    La désactivation coupe immédiatement l'accès (login refusé) mais <strong>préserve toutes les données et traces</strong> du compte (audit, affectations, signatures). Le compte peut être réactivé à tout moment.
                  </div>
                  <div className="space-y-2">
                    <Label>Motif de désactivation <span className="text-red-500">*</span></Label>
                    <Input data-testid="admin-users-toggle-motif" value={toggleMotif} onChange={e => setToggleMotif(e.target.value)} placeholder="Ex : départ, suspension disciplinaire, cessation d'activité…" />
                  </div>
                </>
              ) : (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
                  Le compte retrouvera immédiatement son accès avec ses rôles et rattachements d'origine.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setToggleTarget(null); setToggleMotif(""); }}>Annuler</Button>
            <Button data-testid="admin-users-toggle-submit" onClick={toggleActive}
                    className={toggleTarget?.is_active === false ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}>
              {toggleTarget?.is_active === false ? "Réactiver" : "Désactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetApprove} onOpenChange={v => { if (!v) { setResetApprove(null); setResetTempPwd(""); } }}>
        <DialogContent data-testid="admin-reset-approve-dialog">
          <DialogHeader><DialogTitle>Approuver la réinitialisation</DialogTitle></DialogHeader>
          {resetApprove && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 border p-3">
                <div><span className="text-slate-500">Compte :</span> <code className="ml-2 font-mono">{resetApprove.email}</code></div>
                <div className="text-xs text-slate-500 mt-1">Demande du {new Date(resetApprove.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className="space-y-2">
                <Label>Mot de passe temporaire</Label>
                <div className="flex gap-2">
                  <Input data-testid="admin-reset-pwd" value={resetTempPwd} onChange={e => setResetTempPwd(e.target.value)} className="font-mono" />
                  <Button type="button" variant="outline" onClick={() => setResetTempPwd(genTempPassword())}>Régénérer</Button>
                </div>
                <p className="text-xs text-slate-500">Minimum 10 caractères. Le compte devra le changer à sa prochaine connexion. À transmettre par un canal sécurisé.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetApprove(null); setResetTempPwd(""); }}>Annuler</Button>
            <Button data-testid="admin-reset-approve-submit" onClick={approveReset} disabled={resetTempPwd.length < 10} className="bg-[#1B2A4A] text-white">Approuver et appliquer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lastCreated} onOpenChange={v => { if (!v) setLastCreated(null); }}>
        <DialogContent data-testid="admin-users-lastcreated">
          <DialogHeader><DialogTitle>Compte créé</DialogTitle></DialogHeader>
          {lastCreated && (
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">Email :</span> <code className="ml-2 font-mono">{lastCreated.email}</code></div>
              <div><span className="text-slate-500">Rôle :</span> <Badge className="ml-2">{ROLES.find(r => r.value === lastCreated.role)?.label || lastCreated.role}</Badge></div>
              <div className="rounded-md bg-slate-50 p-3 border">
                <div className="text-xs text-slate-500 mb-1">Mot de passe temporaire :</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-base">{lastCreated.password}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(lastCreated.password)}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Transmettez ce mot de passe par un canal sécurisé (ex. remise en main propre, gestionnaire de mots de passe partagé). Il ne pourra plus être affiché après cette fenêtre. Le compte sera invité à le changer à la première connexion.
              </p>
            </div>
          )}
          <DialogFooter><Button onClick={() => setLastCreated(null)}>J'ai transmis le mot de passe</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
